/**
 * QuestionPaper — a full-page, scrollable "question paper" render of every
 * question in an assignment/test (text + math, options, diagrams). Mount it
 * OUTSIDE the app's sidebar layout so it reads as a clean document and prints
 * cleanly (browser Print → Save as PDF). It does not auto-trigger window.print().
 *
 * Presentational: each app fetches the set and its questions its own way and
 * decides where Back goes. Used by the teacher app's "View in PDF" and the
 * admin console's test detail.
 */

import type { QuestionFigure } from '@padcanvas/shared';
import { useState } from 'react';
import { LatexText } from '../latex';
import { buildQuestionCorrectAnswerText, unplacedFigures, type QuestionPart } from '@padcanvas/shared';
import './QuestionPaperView.css';

type PaperOption = NonNullable<QuestionPart['options']>[number];

/** The fields the paper reads — full question detail, not a list row. */
export interface PaperViewQuestion {
  id: string;
  questionText?: string | null;
  questionType?: string | null;
  questionDiagramUrl?: string | null;
  figures?: QuestionFigure[] | null;
  correctAnswer?: string | null;
  module?: { name?: string } | null;
  options?: PaperOption[] | null;
  parts?: QuestionPart[] | null;
}

/** A question's or a sub-part's options, with the key marked when answers show. */
function PaperOptions({ options, showAnswers, altPrefix }: {
  options: PaperOption[];
  showAnswers: boolean;
  altPrefix: string;
}) {
  return (
    <div className="qpv-options">
      {options.map(opt => {
        const isKey = showAnswers && !!opt.isCorrect;
        return (
        <div key={opt.label} className={`qpv-option${isKey ? ' qpv-option--key' : ''}`}>
          {/* A glyph, not just a colour: browsers drop backgrounds when
              printing unless asked not to, and a key whose marking
              survives on screen but not in the PDF is worse than none. */}
          <span className="qpv-option-mark" aria-hidden="true">{isKey ? '✓' : ''}</span>
          <span className="qpv-option-label">({opt.label})</span>
          <span className="qpv-option-text"><LatexText text={opt.text} /></span>
          {opt.imageUrl && (
            <img src={opt.imageUrl} alt={`${altPrefix} option ${opt.label}`} className="qpv-option-img" />
          )}
          {isKey && <span className="qpv-sr-only">(correct answer)</span>}
        </div>
        );
      })}
    </div>
  );
}

function AnswerLine({ text }: { text: string }) {
  return (
    <div className="qpv-answer">
      <span className="qpv-answer-label">Answer:</span>
      <span className="qpv-answer-text"><LatexText text={text} /></span>
    </div>
  );
}

/** A section of the paper, in order; its questions are numbered on from the last. */
export interface PaperSection {
  id: string;
  title?: string;
  questionIds: string[];
  /** Attempt any `requiredCount` of the section's questions. */
  requiredCount?: number;
  /** Which K count — the first attempted, or the best. */
  countPolicy?: 'best' | 'first';
}

/** The line under a paper's title: a past paper's own name for itself, else
 *  what it is for — exam, classes, subjects. */
export function paperSubtitle(qs: {
  pypRef?: { displayString?: string } | null;
  targetExam?: string | null;
  classes?: Array<string | number> | null;
  subjects?: string[] | null;
}): string {
  if (qs.pypRef?.displayString) return qs.pypRef.displayString;
  const subjects = [...(qs.subjects ?? [])].map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ');
  return [qs.targetExam, qs.classes?.length ? `Class ${qs.classes.join(', ')}` : '', subjects]
    .filter(Boolean).join(' · ');
}

export function QuestionPaper({ title, subtitle, sections, questions, loading, onBack, tariffs }: {
  title: string;
  /** Under the title — e.g. a past paper's "JEE · 2025-04-08 · Shift 2 · Maths". */
  subtitle?: string;
  /** Group under section headings. Absent or empty → one run in `questions` order. */
  sections?: PaperSection[];
  questions: PaperViewQuestion[] | undefined;
  loading: boolean;
  onBack: () => void;
  /** questionId → the question's tariff line ("5 marks — 4 working · option ±1").
   *  The app resolves it — the paper does not know the marking rule. */
  tariffs?: Record<string, string>;
}) {
  /**
   * Off by default, and deliberately so: this page's whole purpose is the paper
   * a student sits, and the cost of it defaulting the wrong way is answers
   * handed out with the question paper. Turning it on makes the same sheet an
   * answer key — the toggle is the only difference between the two documents.
   *
   * Not persisted. A teacher who printed a key last week should still get a
   * blank paper when they open this to print one today.
   */
  const [showAnswers, setShowAnswers] = useState(false);

  // Numbered across sections, and a question that failed to load still takes
  // its number — so Q7 here is Q7 in the test, whichever section it is in.
  const byId = new Map((questions ?? []).map(q => [q.id, q]));
  let counter = 0;
  const groups = sections?.length && questions
    ? sections.map((sec, i) => ({
        key: sec.id,
        title: sec.title || `Section ${i + 1}`,
        note: sec.requiredCount != null && sec.requiredCount < sec.questionIds.length
          ? `attempt any ${sec.requiredCount} of ${sec.questionIds.length}${sec.countPolicy === 'first' ? ` — the first ${sec.requiredCount} attempted count` : ''}`
          : undefined,
        items: sec.questionIds.flatMap(qid => {
          counter += 1;
          const q = byId.get(qid);
          return q ? [{ q, n: counter }] : [];
        }),
      }))
    : [{ key: 'all', title: undefined, note: undefined, items: (questions ?? []).map((q, i) => ({ q, n: i + 1 })) }];

  return (
    <div className="qpv">
      <div className="qpv-toolbar no-print">
        <button className="btn-secondary" onClick={onBack}>&larr; Back</button>
        <span className="qpv-toolbar-title">{title}</span>
        {/* Print takes the DOM as it stands, so honouring this in the PDF is not
            a separate code path — it is the same one. */}
        <label className="qpv-toggle">
          <input
            type="checkbox"
            checked={showAnswers}
            onChange={e => setShowAnswers(e.target.checked)}
          />
          <span>Show answers</span>
        </label>
        <button className="btn-primary" onClick={() => window.print()} disabled={!questions?.length}>
          Print / Save PDF
        </button>
      </div>

      <div className="qpv-paper">
        <h1 className="qpv-title">{title}</h1>
        {subtitle && <p className="qpv-meta">{subtitle}</p>}
        {questions && <p className="qpv-subtitle">{questions.length} Questions</p>}
        {loading && <p className="qpv-muted">Loading questions…</p>}
        {!loading && !questions?.length && <p className="qpv-muted">No questions yet.</p>}

        {groups.map(g => (
          <section key={g.key} className="qpv-section">
            {g.title && (
              <h2 className="qpv-section-title">
                {g.title}
                {g.note && <span className="qpv-section-note"> — {g.note}</span>}
              </h2>
            )}
        {g.items.map(({ q, n }) => {
          // Two shapes of answer, because a paper carries both. An MCQ's answer
          // is WHICH option, so it is marked in place — an "Answer: (c)" line
          // under four options makes the reader count rows to find it. Everything
          // else states a value, which has nowhere to be marked and gets a line.
          const marked = (q.options ?? []).filter(o => o.isCorrect);
          // A STRUCTURED question is laid out as a paper prints it: the stem,
          // then each sub-part on a line of its own with its own options and,
          // on a key, its own answer. `buildQuestionDisplayText` runs the parts
          // into one line, which suits a list row but not a question paper.
          const parts = q.questionType === 'STRUCTURED' ? (q.parts ?? []) : [];
          const answerText = buildQuestionCorrectAnswerText(q).trim();
          // An unanswered question renders as it always did rather than as an
          // empty "Answer:" — a key with blanks in it reads as a broken key,
          // and this paper may well predate anyone authoring the answers.
          // Parts carry their answers under themselves, so the question-level
          // line is only for a STRUCTURED question whose parts state none.
          const partsAnswered = parts.some(p => !!p.correctAnswer?.trim() || p.options?.some(o => o.isCorrect));
          const showLine = showAnswers && marked.length === 0 && !!answerText && !partsAnswered;
          return (
          <div key={q.id} className="qpv-q">
            <div className="qpv-q-head">
              <span className="qpv-q-num">Q{n}.</span>
              {q.module?.name && <span className="qpv-q-tag">{q.module.name}</span>}
              {q.questionType && <span className="qpv-q-type">{q.questionType}</span>}
              {tariffs?.[q.id] && <span className="qpv-q-tariff">{tariffs[q.id]}</span>}
            </div>

            {q.questionText && (
              <div className="qpv-q-text">
                <LatexText text={q.questionText} figures={q.figures} />
              </div>
            )}

            {q.questionDiagramUrl && (
              <div className="qpv-q-img-wrap">
                <img src={q.questionDiagramUrl} alt={`Question ${n}`} className="qpv-q-img" />
              </div>
            )}
            {unplacedFigures(q.questionText, q.figures).map(f => (
              <div key={f.id} className="qpv-q-img-wrap">
                <img src={f.url} alt={f.alt || `Question ${n}`} className="qpv-q-img" />
              </div>
            ))}

            {q.options && q.options.length > 0 && (
              <PaperOptions options={q.options} showAnswers={showAnswers} altPrefix={`Question ${n}`} />
            )}

            {parts.length > 0 && (
              <ol className="qpv-parts">
                {parts.map(p => {
                  const partMarked = (p.options ?? []).some(o => o.isCorrect);
                  const partAnswer = (p.correctAnswer ?? '').trim();
                  return (
                  <li key={p.partId} className="qpv-part">
                    <div className="qpv-part-line">
                      <span className="qpv-part-label">({p.label ?? p.partId})</span>
                      <span className="qpv-part-text">
                        {p.statement?.trim() && <LatexText text={p.statement} />}
                      </span>
                    </div>
                    {p.diagramUrl && (
                      <div className="qpv-q-img-wrap">
                        <img src={p.diagramUrl} alt={`Question ${n} part ${p.partId}`} className="qpv-q-img" />
                      </div>
                    )}
                    {p.options && p.options.length > 0 && (
                      <PaperOptions options={p.options} showAnswers={showAnswers} altPrefix={`Question ${n} part ${p.partId}`} />
                    )}
                    {showAnswers && !partMarked && !!partAnswer && <AnswerLine text={partAnswer} />}
                  </li>
                  );
                })}
              </ol>
            )}

            {showLine && <AnswerLine text={answerText} />}
          </div>
          );
        })}
          </section>
        ))}
      </div>
    </div>
  );
}
